// JS mirror of Inspiration\Creator\Models\Questionnaire\BaseQuestionnaire.
// The PHP class is the source of truth for the schema vocabulary; these
// constants and helpers exist so the same schema serialized into pinia data
// can drive the form renderer, client-side validation, and the admin detail
// page without re-declaring field types or rule logic.

export const FIELD_TYPE_TEXT = "text";
export const FIELD_TYPE_EMAIL = "email";
export const FIELD_TYPE_TEXTAREA = "textarea";
export const FIELD_TYPE_SINGLE_SELECT = "single_select";
export const FIELD_TYPE_MULTI_SELECT = "multi_select";
export const FIELD_TYPE_DROPDOWN = "dropdown";
export const FIELD_TYPE_SUBSCRIPTION_TIERS = "subscription_tiers";
export const FIELD_TYPE_SOCIAL_LINKS = "social_links";
export const FIELD_TYPE_REPEATER_TEXT = "repeater_text";
export const FIELD_TYPE_COLOR_PICKER_LIST = "color_picker_list";
export const FIELD_TYPE_FILE_UPLOAD = "file_upload";
export const FIELD_TYPE_VIBE_PICKER = "vibe_picker";

export const VISIBLE_WHEN_OP_EQUALS = "equals";
export const VISIBLE_WHEN_OP_INCLUDES = "includes";
export const VISIBLE_WHEN_OP_ONE_OF = "one_of";
export const VISIBLE_WHEN_OP_INCLUDES_ANY = "includes_any";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isFieldVisible(field, responseData) {
  if (!field.visible_when) {
    return true;
  }
  const predicate = field.visible_when;
  const sectionData = responseData[predicate.section] || {};
  const actualValue = sectionData[predicate.field];

  if (predicate.operator === VISIBLE_WHEN_OP_EQUALS) {
    return actualValue === predicate.value;
  }
  if (predicate.operator === VISIBLE_WHEN_OP_INCLUDES) {
    return Array.isArray(actualValue) && actualValue.includes(predicate.value);
  }
  if (predicate.operator === VISIBLE_WHEN_OP_ONE_OF) {
    return Array.isArray(predicate.value) && predicate.value.includes(actualValue);
  }
  if (predicate.operator === VISIBLE_WHEN_OP_INCLUDES_ANY) {
    if (!Array.isArray(actualValue) || !Array.isArray(predicate.value)) {
      return false;
    }
    return predicate.value.some(candidate => actualValue.includes(candidate));
  }
  return false;
}

export function isValueFilled(fieldType, value) {
  if (fieldType === FIELD_TYPE_MULTI_SELECT) {
    return Array.isArray(value) && value.length > 0;
  }
  if (fieldType === FIELD_TYPE_EMAIL) {
    return typeof value === "string" && EMAIL_PATTERN.test(value.trim());
  }
  if (fieldType === FIELD_TYPE_REPEATER_TEXT || fieldType === FIELD_TYPE_COLOR_PICKER_LIST) {
    return Array.isArray(value) && value.some(entry => typeof entry === "string" && entry.trim() !== "");
  }
  if (fieldType === FIELD_TYPE_FILE_UPLOAD) {
    return Array.isArray(value) && value.length > 0;
  }
  if (fieldType === FIELD_TYPE_SOCIAL_LINKS) {
    if (!value || typeof value !== "object") {
      return false;
    }
    return Object.values(value).some(entry => typeof entry === "string" && entry.trim() !== "");
  }
  if (typeof value === "string") {
    return value.trim() !== "";
  }
  return value !== null && value !== undefined;
}

export function flattenSections(schema) {
  const sections = [];
  for (const step of schema.steps) {
    for (const section of step.sections) {
      sections.push({ ...section, step: step.step });
    }
  }
  return sections;
}

export function getSection(schema, sectionKey) {
  return flattenSections(schema).find(section => section.key === sectionKey) || null;
}

export function getField(schema, sectionKey, fieldKey) {
  const section = getSection(schema, sectionKey);
  if (!section) {
    return null;
  }
  return section.fields.find(field => field.key === fieldKey) || null;
}

export function resolveFieldLabel(schema, sectionKey, fieldKey) {
  const field = getField(schema, sectionKey, fieldKey);
  return field ? field.label : null;
}

export function resolveOptionLabel(schema, sectionKey, fieldKey, value) {
  const field = getField(schema, sectionKey, fieldKey);
  if (!field || !field.options) {
    return null;
  }
  const match = field.options.find(option => option.value === value);
  return match ? match.label : null;
}

function getRequiredOneOfGroups(tierFields) {
  const groups = new Map();
  for (const tierField of tierFields) {
    if (tierField.required_one_of_group) {
      const groupKey = tierField.required_one_of_group;
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey).push(tierField);
    }
  }
  return groups;
}

function collectSubscriptionTierMissingKeys(sectionKey, field, tiersValue) {
  const missing = [];
  const tiers = Array.isArray(tiersValue) ? tiersValue : [];
  const groups = getRequiredOneOfGroups(field.tier_fields);

  tiers.forEach((tier, tierIndex) => {
    for (const tierField of field.tier_fields) {
      if (tierField.required_one_of_group) {
        continue;
      }
      if (!tierField.required) {
        continue;
      }
      const tierValue = tier[tierField.key];
      const fieldType = tierField.type || FIELD_TYPE_TEXT;
      if (!isValueFilled(fieldType, tierValue)) {
        missing.push(`${sectionKey}.${field.key}[${tierIndex}].${tierField.key}`);
      }
    }

    for (const [groupKey, groupFields] of groups.entries()) {
      const anyFilled = groupFields.some(tierField => {
        const fieldType = tierField.type || FIELD_TYPE_TEXT;
        return isValueFilled(fieldType, tier[tierField.key]);
      });
      if (!anyFilled) {
        missing.push(`${sectionKey}.${field.key}[${tierIndex}].${groupKey}`);
      }
    }
  });

  return missing;
}

// Returns dotted-path keys for required+visible fields that are not yet filled.
// Pass `stepNumber` to scope to a single step (the form's per-step validation);
// omit it for whole-form validation.
export function getMissingFieldKeys(schema, responseData, stepNumber = null) {
  const missing = [];

  for (const step of schema.steps) {
    if (stepNumber !== null && step.step !== stepNumber) {
      continue;
    }
    for (const section of step.sections) {
      const sectionData = responseData[section.key] || {};

      for (const field of section.fields) {
        if (!isFieldVisible(field, responseData)) {
          continue;
        }

        if (field.type === FIELD_TYPE_SUBSCRIPTION_TIERS) {
          const tierMissing = collectSubscriptionTierMissingKeys(section.key, field, sectionData[field.key]);
          tierMissing.forEach(key => missing.push(key));
          continue;
        }

        if (!field.required) {
          continue;
        }

        if (!isValueFilled(field.type, sectionData[field.key])) {
          missing.push(`${section.key}.${field.key}`);
        }
      }
    }
  }

  return missing;
}

export function buildEmptyResponseData(schema) {
  const responseData = {};
  for (const step of schema.steps) {
    for (const section of step.sections) {
      responseData[section.key] = responseData[section.key] || {};
      for (const field of section.fields) {
        responseData[section.key][field.key] = buildEmptyValueForField(field);
      }
    }
  }
  return responseData;
}

export function buildEmptyValueForField(field) {
  if (field.type === FIELD_TYPE_MULTI_SELECT) {
    return [];
  }
  if (field.type === FIELD_TYPE_SUBSCRIPTION_TIERS) {
    return [buildEmptyTier(field)];
  }
  if (field.type === FIELD_TYPE_REPEATER_TEXT || field.type === FIELD_TYPE_COLOR_PICKER_LIST) {
    return [""];
  }
  if (field.type === FIELD_TYPE_FILE_UPLOAD) {
    return [];
  }
  if (field.type === FIELD_TYPE_SOCIAL_LINKS) {
    const platforms = Array.isArray(field.social_platforms) ? field.social_platforms : [];
    const value = {};
    for (const platform of platforms) {
      value[platform.value] = "";
    }
    return value;
  }
  return "";
}

export function buildEmptyTier(subscriptionTiersField) {
  const tier = {};
  for (const tierField of subscriptionTiersField.tier_fields) {
    tier[tierField.key] = "";
  }
  return tier;
}
